/*
 * Bundle links: one URL that lists several products so a shopper can add them all at once.
 *
 *   /pages/bundle?items=44812345x2,44867890&title=Attacking+setup&note=...&code=WELCOME10
 *
 * items  variant IDs, comma separated, each with an optional "x<qty>" (default 1)
 * title  heading shown on the page (falls back to the Shopify page title)
 * note   a line from the shop under the heading
 * code   discount code applied to the cart when the bundle is added
 *
 * The owner builds links on /pages/bundle-builder (pk-bundle-builder.js). The page reads
 * product data from the catalog that pk-bundle-catalog.liquid embeds, so no product data
 * travels in the link. Title and note do: they render as plain text only.
 */
(function () {
  const root = window.Shopify?.routes?.root || '/';
  const LIMITS = { items: 30, qty: 20, title: 80, note: 280, code: 40 };
  const ITEM_PATTERN = /^(\d{1,20})(?:x(\d{1,3}))?$/;
  const CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

  function clampQty(qty) {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, LIMITS.qty);
  }

  // Same variant listed twice adds up into one line, the way the cart would merge them.
  function parseItems(value) {
    const items = [];
    const byId = new Map();
    for (const part of String(value || '').split(',')) {
      const match = ITEM_PATTERN.exec(part.trim());
      if (!match) continue;
      const id = match[1];
      const qty = clampQty(match[2] || 1);
      const existing = byId.get(id);
      if (existing) {
        existing.qty = clampQty(existing.qty + qty);
        continue;
      }
      if (items.length >= LIMITS.items) break;
      const item = { id, qty };
      byId.set(id, item);
      items.push(item);
    }
    return items;
  }

  function serializeItems(items) {
    return items.map(item => (item.qty > 1 ? `${item.id}x${item.qty}` : String(item.id))).join(',');
  }

  function cleanText(value, max) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
  }

  function cleanCode(value) {
    const code = String(value || '').trim();
    return code && code.length <= LIMITS.code && CODE_PATTERN.test(code) ? code : '';
  }

  function readBundle(search) {
    const params = new URLSearchParams(search);
    return {
      items: parseItems(params.get('items')),
      title: cleanText(params.get('title'), LIMITS.title),
      note: cleanText(params.get('note'), LIMITS.note),
      code: cleanCode(params.get('code'))
    };
  }

  // Commas stay literal so the item list is readable when the owner pastes the link.
  function bundleQuery(bundle) {
    const params = new URLSearchParams();
    params.set('items', serializeItems(bundle.items));
    const title = cleanText(bundle.title, LIMITS.title);
    const note = cleanText(bundle.note, LIMITS.note);
    const code = cleanCode(bundle.code);
    if (title) params.set('title', title);
    if (note) params.set('note', note);
    if (code) params.set('code', code);
    return params.toString().replace(/%2C/gi, ',');
  }

  function indexCatalog(products) {
    const variants = new Map();
    for (const product of products || []) {
      for (const variant of product.variants || []) {
        variants.set(String(variant.id), { product, variant });
      }
    }
    return variants;
  }

  function readCatalog(doc) {
    const el = doc.getElementById('pkBundleCatalog');
    try {
      return el ? JSON.parse(el.textContent) : [];
    } catch (err) {
      console.error('[PKBundle] Catalog is not valid JSON:', err);
      return [];
    }
  }

  // Shopify money formats: "Rs. {{amount}}", "₹{{amount_no_decimals}}", etc.
  function formatMoney(cents, format) {
    const template = format || 'Rs. {{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, name) => {
      if (name === 'amount_no_decimals') return group(0, ',', '.');
      if (name === 'amount_with_comma_separator') return group(2, '.', ',');
      if (name === 'amount_no_decimals_with_comma_separator') return group(0, '.', ',');
      return group(2, ',', '.');
    });
  }

  function variantLabel(product, variant) {
    return product.single ? '' : variant.title;
  }

  async function requestJSON(path, options = {}) {
    const headers = { Accept: 'application/json' };
    if (options.body) headers['Content-Type'] = 'application/json';
    const response = await fetch(root + path, { ...options, cache: 'no-store', headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your cart. Please try again.');
    return data;
  }

  // Adds every line in one request, so the shopper never ends up with half a bundle.
  // A code already in the cart is kept: /cart/update.js replaces the whole list.
  async function addBundle(lines, code) {
    const result = await requestJSON('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: lines.map(line => ({ id: Number(line.id), quantity: line.qty })) })
    });
    if (code) {
      try {
        const cart = await requestJSON('cart.js');
        const codes = (cart.discount_codes || []).map(d => d.code);
        if (!codes.some(c => c.toLowerCase() === code.toLowerCase())) {
          await requestJSON('cart/update.js', {
            method: 'POST',
            body: JSON.stringify({ discount: [...codes, code].join(',') })
          });
        }
      } catch (err) {
        // The products are in the cart; the shopper can still type the code at checkout.
        console.error('[PKBundle] Failed to apply discount code:', err);
      }
    }
    return result;
  }

  /* ---------- Bundle page ---------- */

  function el(doc, tag, className, text) {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function initBundlePage(section) {
    const doc = section.ownerDocument;
    const format = section.dataset.moneyFormat;
    const freeOver = Number(section.dataset.freeOver) || 0;
    const bundle = readBundle(window.location.search);
    const catalog = indexCatalog(readCatalog(doc));
    const q = selector => section.querySelector(selector);

    if (bundle.title) {
      q('[data-bundle-title]').textContent = bundle.title;
      doc.title = `${bundle.title} – ${doc.title}`;
    }
    if (bundle.note) {
      const note = q('[data-bundle-note]');
      note.textContent = bundle.note;
      note.hidden = false;
    }

    const lines = [];
    let missing = 0;
    for (const item of bundle.items) {
      const found = catalog.get(item.id);
      if (!found) {
        missing += 1;
        continue;
      }
      lines.push({
        product: found.product,
        variant: found.variant,
        qty: item.qty,
        include: found.variant.available
      });
    }

    if (!lines.length) {
      q('[data-bundle-empty]').hidden = false;
      q('[data-bundle-layout]').hidden = true;
      return;
    }

    if (missing) {
      const warn = q('[data-bundle-missing]');
      warn.textContent = missing === 1
        ? 'One product in this bundle is no longer sold, so it has been left out.'
        : `${missing} products in this bundle are no longer sold, so they have been left out.`;
      warn.hidden = false;
    }

    const list = q('[data-bundle-list]');
    const addButton = q('[data-bundle-add]');
    const buyButton = q('[data-bundle-buy]');
    const errorEl = q('[data-bundle-error]');

    lines.forEach((line, index) => list.appendChild(renderLine(doc, line, index, format, update)));

    if (bundle.code) {
      const codeEl = q('[data-bundle-code]');
      codeEl.textContent = `Discount code ${bundle.code} is applied when you add this bundle. The discount shows at checkout.`;
      codeEl.hidden = false;
    }

    function selected() {
      return lines.filter(line => line.include && line.variant.available);
    }

    function update() {
      const chosen = selected();
      const count = chosen.reduce((sum, line) => sum + line.qty, 0);
      const total = chosen.reduce((sum, line) => sum + line.variant.price * line.qty, 0);
      const compare = chosen.reduce(
        (sum, line) => sum + Math.max(line.variant.compare, line.variant.price) * line.qty, 0);
      q('[data-bundle-count]').textContent = count === 1 ? '1 item' : `${count} items`;
      q('[data-bundle-total]').textContent = formatMoney(total, format);
      const saveRow = q('[data-bundle-save-row]');
      saveRow.hidden = compare <= total;
      q('[data-bundle-save]').textContent = formatMoney(compare - total, format);
      const ship = q('[data-bundle-ship]');
      ship.textContent = freeOver && total > freeOver
        ? 'Free shipping on this bundle'
        : 'Shipping shows in your cart before checkout';
      addButton.disabled = buyButton.disabled = !count;
      addButton.textContent = count ? `Add ${count === 1 ? '1 item' : count + ' items'} to cart` : 'Choose at least one item';
    }

    async function submit(goToCheckout) {
      const chosen = selected();
      if (!chosen.length) return;
      errorEl.hidden = true;
      const button = goToCheckout ? buyButton : addButton;
      const label = button.textContent;
      addButton.disabled = buyButton.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'ADDING…';
      try {
        await addBundle(chosen.map(line => ({ id: line.variant.id, qty: line.qty })), bundle.code);
        window.posthog?.capture?.('Bundle Added', {
          bundle_title: bundle.title || null,
          variant_ids: chosen.map(line => String(line.variant.id)),
          items_offered: lines.length,
          items_added: chosen.length,
          checkout: goToCheckout
        });
        if (goToCheckout) {
          window.location.assign(root + 'checkout');
          return;
        }
        if (window.PKCart) await window.PKCart.open();
        else window.location.assign(root + 'cart');
      } catch (err) {
        errorEl.textContent = err.message || 'Unable to update your cart. Please try again.';
        errorEl.hidden = false;
        console.error('[PKBundle] Failed to add bundle:', err);
      } finally {
        button.removeAttribute('aria-busy');
        button.textContent = label;
        update();
      }
    }

    addButton.addEventListener('click', () => submit(false));
    buyButton.addEventListener('click', () => submit(true));
    q('[data-bundle-layout]').hidden = false;
    update();
  }

  function renderLine(doc, line, index, format, onChange) {
    const { product } = line;
    const li = el(doc, 'li', 'pk-bundle__line');
    const id = `pkBundleLine${index}`;

    const check = el(doc, 'input', 'pk-bundle__check');
    check.type = 'checkbox';
    check.id = id;
    check.checked = line.include;
    check.setAttribute('aria-label', `Include ${product.title}`);

    const imgWrap = el(doc, 'a', 'pk-bundle__img');
    imgWrap.href = product.url;
    imgWrap.tabIndex = -1;
    imgWrap.setAttribute('aria-hidden', 'true');
    const img = el(doc, 'img');
    img.alt = '';
    img.loading = 'lazy';
    img.width = 160;
    img.height = 160;
    imgWrap.appendChild(img);

    const body = el(doc, 'div', 'pk-bundle__body');
    if (product.vendor) body.appendChild(el(doc, 'p', 'pk-bundle__vendor', product.vendor));
    const name = el(doc, 'a', 'pk-bundle__name', product.title);
    name.href = product.url;
    body.appendChild(name);

    let select = null;
    if (!product.single && product.variants.length > 1) {
      select = el(doc, 'select', 'pk-bundle__variant');
      select.setAttribute('aria-label', `Option for ${product.title}`);
      for (const variant of product.variants) {
        const option = el(doc, 'option', '', variant.available ? variant.title : `${variant.title} – sold out`);
        option.value = String(variant.id);
        option.disabled = !variant.available;
        option.selected = variant.id === line.variant.id;
        select.appendChild(option);
      }
      body.appendChild(select);
    } else if (variantLabel(product, line.variant)) {
      body.appendChild(el(doc, 'p', 'pk-bundle__option', line.variant.title));
    }
    const status = el(doc, 'p', 'pk-bundle__oos');
    body.appendChild(status);

    const side = el(doc, 'div', 'pk-bundle__side');
    const qty = el(doc, 'p', 'pk-bundle__qty');
    const price = el(doc, 'p', 'pk-bundle__price');
    side.append(qty, price);

    function paint() {
      const { variant } = line;
      img.src = variant.image || product.image || '';
      img.hidden = !img.src;
      qty.textContent = line.qty > 1 ? `× ${line.qty}` : '';
      qty.hidden = line.qty < 2;
      price.textContent = formatMoney(variant.price * line.qty, format);
      status.textContent = variant.available ? '' : 'Sold out';
      status.hidden = variant.available;
      check.disabled = !variant.available;
      check.checked = line.include && variant.available;
      li.classList.toggle('is-off', !check.checked);
    }

    check.addEventListener('change', () => {
      line.include = check.checked;
      paint();
      onChange();
    });
    select?.addEventListener('change', () => {
      line.variant = product.variants.find(v => String(v.id) === select.value) || line.variant;
      if (line.variant.available) line.include = true;
      paint();
      onChange();
    });

    // A label around the checkbox gives it a finger-sized tap target.
    const label = el(doc, 'label', 'pk-bundle__pick');
    label.htmlFor = id;
    label.appendChild(check);
    li.append(label, imgWrap, body, side);
    paint();
    return li;
  }

  window.PKBundle = {
    LIMITS, parseItems, serializeItems, readBundle, bundleQuery,
    indexCatalog, readCatalog, formatMoney, variantLabel
  };

  function start() {
    document.querySelectorAll('[data-pk-bundle]').forEach(initBundlePage);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
